import { z } from 'zod';

const TransactionSchema = z.object({
  id: z.string(),
  type: z.enum(['income', 'expense']),
  amount: z.number(),
  date: z.string(),
  category: z.string(),
  description: z.string(),
  taxDeductible: z.boolean(),
  superContribution: z.number().optional(),
  accountId: z.string().optional(),
  createdAt: z.string(),
});

const AccountSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.enum(['cash', 'savings', 'transaction', 'credit_card', 'other']),
  initialBalance: z.number(),
  createdAt: z.string(),
});

const TransferSchema = z.object({
  id: z.string(),
  fromAccountId: z.string(),
  toAccountId: z.string(),
  amount: z.number(),
  date: z.string(),
  description: z.string(),
  createdAt: z.string(),
});

const BudgetSchema = z.object({
  id: z.string(),
  category: z.string(),
  monthlyLimit: z.number(),
  month: z.string(),
});

const BillSchema = z.object({
  id: z.string(),
  name: z.string(),
  amount: z.number(),
  dueDate: z.string(),
  frequency: z.enum(['weekly', 'fortnightly', 'monthly', 'quarterly', 'yearly']),
  paid: z.boolean(),
  lastPaidDate: z.string().optional(),
});

const SavingsGoalSchema = z.object({
  id: z.string(),
  name: z.string(),
  targetAmount: z.number(),
  currentAmount: z.number(),
  targetDate: z.string(),
  contributionAmount: z.number().optional(),
  contributionFrequency: z.enum(['weekly', 'fortnightly', 'monthly', 'none']).optional(),
  lastContributionDate: z.string().optional(),
  createdAt: z.string(),
});

const AssetSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  purchaseDate: z.string(),
  purchasePrice: z.number(),
  currentValue: z.number(),
  photo: z.string().optional(),
  room: z.string(),
  insurerName: z.string().optional(),
  policyNumber: z.string().optional(),
  createdAt: z.string(),
});

const PaydayConfigSchema = z.object({
  mode: z.enum(['auto', 'manual']),
  frequency: z.enum(['weekly', 'fortnightly', 'monthly']).optional(),
  nextDate: z.string().optional(),
  amount: z.number().optional(),
}).optional();

const AppSettingsSchema = z.object({
  autoLockMinutes: z.number(),
  darkMode: z.boolean(),
  paydayConfig: PaydayConfigSchema,
  achievementsResetAt: z.string().optional(),
});

const VehicleSchema = z.object({
  id: z.string(),
  name: z.string(),
  rego: z.string().optional(),
  workUsePercent: z.number(),
  createdAt: z.string(),
});

const VehicleExpenseSchema = z.object({
  id: z.string(),
  vehicleId: z.string(),
  type: z.enum(['fuel', 'rego', 'insurance', 'servicing', 'tolls', 'parking', 'other']),
  amount: z.number(),
  date: z.string(),
  description: z.string(),
  odometer: z.number().optional(),
  createdAt: z.string(),
});

const SavingsChallengeSchema = z.object({
  id: z.string(),
  type: z.enum(['52week', 'roundup', 'nospend']),
  name: z.string(),
  startDate: z.string(),
  completedWeeks: z.array(z.boolean()).optional(),
  roundupTotal: z.number().optional(),
  noSpendDays: z.array(z.string()).optional(),
  noSpendTarget: z.number().optional(),
  active: z.boolean(),
  createdAt: z.string(),
});

export const AppDataSchema = z.object({
  transactions: z.array(TransactionSchema),
  budgets: z.array(BudgetSchema),
  bills: z.array(BillSchema),
  assets: z.array(AssetSchema),
  accounts: z.array(AccountSchema),
  transfers: z.array(TransferSchema),
  savingsGoals: z.array(SavingsGoalSchema),
  vehicles: z.array(VehicleSchema).default([]),
  vehicleExpenses: z.array(VehicleExpenseSchema).default([]),
  savingsChallenges: z.array(SavingsChallengeSchema).default([]),
  categories: z.array(z.string()),
  settings: AppSettingsSchema,
});
